import { InputV2 } from '@cimplico/components';
import { PencilIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectListItemNames, selectRenamingListItem } from '../../features/worksheet-v2/custom-worksheet-v2/store/selectors';
import { editPropertyName, setRenamingPropertyId } from '../../features/worksheet-v2/custom-worksheet-v2/store/slice';
import { WorksheetSchemaProperty } from '../../types/custom-worksheet-v2';
import { isField, isListItem } from '../../features/worksheet-v2/custom-worksheet-v2/store/utils';
import { getZIndex } from '../../features/worksheet-v2/custom-worksheet-v2/utils';
import { isKeyword } from './utils';
import { PROPERTY_NAME_CHAR_LIMIT } from '../../constants';
import CWInputControlWrapper from './CWInputControlWrapper';
import NavItem from '../../features/worksheet-v2/custom-worksheet-v2/tree/NavItem';

type CWPropertyNameProps = {
    canRename?: boolean;
    allowRedirect?: boolean;
    headingSize: 'xl' | '2xl' | 'base' | 'sm' | 'lg';
    property: WorksheetSchemaProperty;
};

function CWPropertyName({ canRename = false, allowRedirect, headingSize, property }: CWPropertyNameProps): JSX.Element {
    const dispatch = useDispatch();
    const currentRenamingListItem = useSelector(selectRenamingListItem);
    const allListItemNames = useSelector(selectListItemNames(property));

    const [name, setName] = useState(property.name);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    const isRenaming = currentRenamingListItem === property.id;

    const zIndex = getZIndex(property.id);

    /**
     * On escape key/cancel button click, revert list item name changes
     * Clear error message and clear renaming list item id state
     */
    function handleResetValue(): void {
        setErrorMessage(undefined);
        setName(property.name);
        dispatch(setRenamingPropertyId(undefined));
    }

    /**
     * Save new list item name value
     * Validate new name value
     */
    function handleSave(ignoreError?: boolean): void {
        if (!isRenaming) {
            return;
        }

        let duplicateName = false;
        const updatedName = name.trim();

        // If we are updating the list item name, check if it is a unique name
        // Field name doesn't need to be unique
        if (isListItem(property)) {
            duplicateName = updatedName !== property.name && allListItemNames.includes(updatedName.toLowerCase());
        }

        if (isKeyword(updatedName)) {
            setErrorMessage(`Invalid name: ${updatedName} is a keyword`);
            return;
        }

        // If name value is an empty string, revert to the previous value
        if (updatedName === '' || (duplicateName && ignoreError)) {
            setName(property.name);
            dispatch(setRenamingPropertyId(undefined));
            return;
        }

        // If new name value is not unique, show error message
        if (duplicateName) {
            setErrorMessage('List item name must be unique within a list.');
            return;
        }

        dispatch(editPropertyName({ id: property.id, value: updatedName }));
        dispatch(setRenamingPropertyId(undefined));
    }

    return (
        <>
            {isRenaming ? (
                <div className="w-[300px]">
                    <CWInputControlWrapper zIndex={zIndex} onSave={handleSave} onCancel={handleResetValue}>
                        <InputV2
                            autoFocus
                            placeholder="Enter a name..."
                            value={name}
                            showCharacterLimitHint={false}
                            characterLimit={PROPERTY_NAME_CHAR_LIMIT}
                            type="text"
                            errorMessage={errorMessage}
                            onChange={(val): void => {
                                setName(val);
                                setErrorMessage(undefined);
                            }}
                        />
                    </CWInputControlWrapper>
                </div>
            ) : (
                <div
                    className={`${
                        canRename ? '-mr-9 py-1 px-2 cursor-pointer hover:bg-gray-200 group rounded-[5px] z-10' : ''
                    } flex gap-3 items-center border border-transparent`}
                    onClick={(): void => {
                        if (canRename) {
                            setName(property.name);
                            setErrorMessage(undefined);
                            dispatch(setRenamingPropertyId(property.id));
                        }
                    }}
                >
                    {allowRedirect ? (
                        <NavItem id={property.id}>
                            <p className={`text-indigo-600 text-${headingSize} hover:underline`}>{property.name}</p>
                        </NavItem>
                    ) : (
                        <>
                            <p
                                className={`${headingSize === 'sm' ? 'text-gray-600' : 'text-gray-800'} ${
                                    isField(property) ? '' : 'font-medium'
                                } text-${headingSize}`}
                            >
                                {property.name}
                            </p>
                            {canRename && <PencilIcon className="w-4 h-4 text-transparent group-hover:text-gray-500" />}
                        </>
                    )}
                </div>
            )}
        </>
    );
}

export default CWPropertyName;
